import AdminTable from '../admin/AdminTable';
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    FileText,
    FileSpreadsheet,
    Calendar,
    User,
    DollarSign,
    CreditCard,
    CheckCircle,
    AlertCircle,
    ArrowLeft,
    X
} from 'lucide-react';
import { fetchReportePagoPorCitaId, ReportePago } from '../../services/reportePagosService';
import { exportToPDF, exportToExcel } from './exportadores';
import './HistorialPagosPage.css';

// Función auxiliar para el badge de estado (copiada de HistorialPagosPage)
const getEstadoBadge = (estado: string) => {
    const estadoLower = estado?.toLowerCase() ?? '';
    switch (estadoLower) {
        case 'completo':
            return (
                <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-[#8b3fa8]/10 text-[#61257b] border border-[#8b3fa8]/20">
                    <CheckCircle className="mr-1.5 h-3 w-3" />
                    Pagado
                </span>
            );
        case 'parcial':
            return (
                <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-[#ffd97c]/10 text-[#ea8406] border border-[#ffd97c]/20">
                    <AlertCircle className="mr-1.5 h-3 w-3" />
                    Parcial
                </span>
            );
        default:
            return (
                <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-[#c1348b]/10 text-[#8f1f64] border border-[#c1348b]/20">
                    Pendiente
                </span>
            );
    }
};

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-BO', {
        style: 'currency',
        currency: 'BOB'
    }).format(amount);
};

const HistorialPagosCitasPage = () => {
    const { citaId } = useParams<{ citaId: string }>();
    const [reporte, setReporte] = useState<ReportePago | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!citaId) return;

        const cargarReporte = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const data = await fetchReportePagoPorCitaId(parseInt(citaId));
                setReporte(data);
            } catch (err: any) {
                setError(err.response?.data?.error || 'Error al cargar el reporte de pagos');
                setReporte(null);
            } finally {
                setIsLoading(false);
            }
        };

        cargarReporte();
    }, [citaId]);

    const handleExportarPDF = () => {
        if (reporte) exportToPDF(reporte);
    };

    const handleExportarExcel = () => {
        if (reporte) exportToExcel(reporte);
    };

    if (isLoading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                    <div className="flex justify-center items-center">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#61257b] mx-auto mb-4"></div>
                            <p className="text-gray-500 font-medium">Cargando reporte de pagos...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !reporte) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                        <X className="h-8 w-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        No se pudo cargar el reporte
                    </h3>
                    <p className="text-gray-500 max-w-md mx-auto mb-6">
                        {error || 'El reporte de pagos no está disponible para esta cita.'}
                    </p>
                    <Link
                        to={`/reservas`}
                        className="px-6 py-3 bg-[#61257b] text-white font-medium rounded-lg hover:bg-[#0066a6] transition-all shadow-sm"
                    >
                        Volver a la reserva
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
            {/* Header con botón de volver */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-gray-50 to-gray-100 p-8 border border-gray-200 shadow-xl">
                <div className="absolute -top-16 -right-16 w-80 h-80 bg-gradient-to-br from-[#c1348b]/20 via-[#c1348b]/10 to-[#61257b]/20 rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-48 bg-gradient-to-r from-[#61257b]/5 via-transparent to-[#c1348b]/5 blur-2xl"></div>

                <div className="relative z-10">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-6">
                        <div className="flex-1">
                            <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-[#61257b]/10 to-[#8b3fa8]/10 rounded-full border border-[#61257b]/20 mb-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-[#61257b] rounded-full"></div>
                                    <div className="w-2 h-2 bg-[#c1348b] rounded-full animate-pulse"></div>
                                    <div className="w-2 h-2 bg-[#8b3fa8] rounded-full"></div>
                                </div>
                                <span className="text-sm font-medium text-[#61257b]">Gestión de Pagos</span>
                            </div>

                            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                                <div>
                                    <h1 className="text-4xl font-bold text-gray-900 mb-3">
                                        Historial de Pagos por Sesiones
                                    </h1>
                                    <p className="text-gray-700 text-lg max-w-2xl">
                                        Visualizando pagos de la cita: {reporte.codigo}
                                    </p>
                                </div>

                                {/* Botón de volver a la reserva */}
                                <div className="flex-shrink-0">
                                    <Link
                                        to={`/reservas`}
                                        className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 hover:border-white/40 text-gray-700 hover:text-gray-900 transition-all duration-300 shadow-sm hover:shadow"
                                        title="Volver a la reserva"
                                    >
                                        <ArrowLeft className="h-5 w-5" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Línea decorativa */}
                    <div className="mt-6 pt-6 border-t border-gray-200/50">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-3 h-3 rounded-full bg-[#61257b]"></div>
                                <span>Visualización detallada</span>
                            </div>
                            <div className="h-4 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-3 h-3 rounded-full bg-[#c1348b]"></div>
                                <span>Reportes exportables</span>
                            </div>
                            <div className="h-4 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-3 h-3 rounded-full bg-[#8b3fa8]"></div>
                                <span>Por sesión individual</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Reporte */}
            <div className="space-y-8">
                {/* Resumen */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-[#61257b]/5 to-[#8b3fa8]/5">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                    Cita: {reporte.codigo}
                                </h2>
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                    <span className="flex items-center gap-1">
                                        <User className="h-4 w-4" />
                                        {reporte.cliente_info.nombre}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <DollarSign className="h-4 w-4" />
                                        Total: {formatCurrency(Number(reporte.total))}
                                    </span>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={handleExportarPDF}
                                    className="px-4 py-2.5 bg-[#c1348b] text-white font-medium rounded-lg hover:bg-[#e01c4a] focus:outline-none focus:ring-2 focus:ring-[#c1348b] focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors duration-300 shadow-sm hover:shadow"
                                >
                                    <FileText className="h-4 w-4" />
                                    Exportar PDF
                                </button>
                                <button
                                    onClick={handleExportarExcel}
                                    className="px-4 py-2.5 bg-[#8b3fa8] text-white font-medium rounded-lg hover:bg-[#00b8a0] focus:outline-none focus:ring-2 focus:ring-[#8b3fa8] focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors duration-300 shadow-sm hover:shadow"
                                >
                                    <FileSpreadsheet className="h-4 w-4" />
                                    Exportar Excel
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="p-6">
                        {/* Cards de resumen */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                <p className="text-sm font-medium text-gray-600 mb-1">Total Pagado</p>
                                <p className="text-2xl font-bold text-[#61257b]">
                                    {formatCurrency(Number(reporte.monto_pagado))}
                                </p>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                <p className="text-sm font-medium text-gray-600 mb-1">Deuda Total</p>
                                <p className={`text-2xl font-bold ${reporte.deuda_total > 0 ? 'text-[#c1348b]' : 'text-[#8b3fa8]'}`}>
                                    {formatCurrency(Number(reporte.deuda_total))}
                                </p>
                                {reporte.deuda_total <= 0 && (
                                    <p className="text-xs text-[#8b3fa8] mt-1 flex items-center gap-1">
                                        <CheckCircle className="h-3 w-3" />
                                        Sin deudas pendientes
                                    </p>
                                )}
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                <p className="text-sm font-medium text-gray-600 mb-1">Porcentaje Pagado</p>
                                <p className="text-2xl font-bold text-[#8f1f64]">
                                    {((reporte.monto_pagado / reporte.total) * 100).toFixed(1)}%
                                </p>
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                    <div
                                        className="bg-gradient-to-r from-[#8b3fa8] to-[#61257b] h-2 rounded-full"
                                        style={{ width: `${(reporte.monto_pagado / reporte.total) * 100}%` }}
                                    ></div>
                                </div>
                            </div>
                        </div>

                        {/* Detalle por Sesiones */}
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalle por Sesiones</h3>
                        <div className="space-y-4">
                            {reporte.sesiones_detalle.map((sesion, index) => (
                                <div key={sesion.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Calendar className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <h4 className="font-medium text-gray-900">
                                                    Sesión {index + 1}: {new Date(sesion.fecha_inicio).toLocaleDateString('es-BO', { timeZone: 'UTC' })}
                                                    {sesion.fecha_fin !== sesion.fecha_inicio &&
                                                        ` - ${new Date(sesion.fecha_fin).toLocaleDateString('es-BO', { timeZone: 'UTC' })}`
                                                    }
                                                </h4>
                                                <p className="text-sm text-gray-600">
                                                    {sesion.horas} hora{sesion.horas !== 1 ? 's' : ''}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <p className="text-sm text-gray-600">Precio con descuento</p>
                                                <p className="font-medium text-gray-900">
                                                    {formatCurrency(Number(sesion.precio_con_descuento))}
                                                </p>
                                            </div>
                                            {getEstadoBadge(sesion.estado_pago)}
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                            <div className="text-center p-3 bg-gradient-to-r from-[#61257b]/5 to-[#8b3fa8]/5 rounded-lg border border-[#8b3fa8]/20">
                                                <p className="text-sm text-gray-600">Pagado en esta sesión</p>
                                                <p className="text-lg font-semibold text-[#61257b]">
                                                    {formatCurrency(Number(sesion.monto_pagado))}
                                                </p>
                                            </div>
                                            <div className="text-center p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                                                <p className="text-sm text-gray-600">Saldo pendiente</p>
                                                <p className={`text-lg font-semibold ${sesion.saldo_pendiente > 0 ? 'text-[#c1348b]' : 'text-[#8b3fa8]'}`}>
                                                    {formatCurrency(Number(sesion.saldo_pendiente))}
                                                </p>
                                            </div>
                                            <div className="text-center p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                                                <p className="text-sm text-gray-600">Pagos registrados</p>
                                                <p className="text-lg font-semibold text-gray-900">
                                                    {sesion.pagos.length}
                                                </p>
                                            </div>
                                        </div>

                                        {sesion.pagos.length > 0 ? (
                                            <div>
                                                <h5 className="text-sm font-medium text-gray-700 mb-2">Pagos realizados:</h5>
                                                <div className="space-y-2">
                                                    {sesion.pagos.map((pago) => (
                                                        <div key={pago.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                            <div className="flex items-center gap-3">
                                                                <CreditCard className="h-4 w-4 text-gray-500" />
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">
                                                                        {new Date(pago.fecha_pago).toLocaleDateString('es-BO')}
                                                                        {' '}
                                                                        {new Date(pago.fecha_pago).toLocaleTimeString('es-BO', {
                                                                            hour: '2-digit',
                                                                            minute: '2-digit'
                                                                        })}
                                                                    </p>
                                                                    <p className="text-xs text-gray-600">{pago.tipo_pago_nombre}</p>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-sm font-semibold text-[#61257b]">
                                                                    {formatCurrency(Number(pago.monto))}
                                                                </p>
                                                                <p className="text-xs text-gray-500">Código: {pago.codigo}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4 text-gray-500">
                                                No se han registrado pagos específicos para esta sesión
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Historial completo de pagos */}
                        <div className="mt-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Historial Completo de Pagos</h3>
                            <div className="overflow-x-auto">
                                <AdminTable className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gradient-to-r from-[#61257b]/5 to-[#8b3fa8]/5">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Fecha y Hora
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Origen
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Tipo de Pago
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Monto
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Código
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {reporte.historial_pagos.map((pago) => (
                                            <tr key={pago.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {new Date(pago.fecha_pago).toLocaleDateString('es-BO', { timeZone: 'UTC' })}
                                                    <br />
                                                    <span className="text-gray-500">
                                                        {new Date(pago.fecha_pago).toLocaleTimeString('es-BO', {
                                                            timeZone: 'UTC',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {pago.origen}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {pago.tipo_pago_nombre}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-[#61257b]">
                                                    {formatCurrency(Number(pago.monto))}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                                    {pago.codigo}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </AdminTable>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HistorialPagosCitasPage;
